import { Link } from "react-router-dom";
import type { Book } from "../api";
import { useT } from "../i18n";
import { initialOf, rampFor } from "./cover";
import { LockIcon } from "./icons";
import Logo from "./Logo";
import ProgressBar from "./ProgressBar";
import SaveButton from "./SaveButton";
import SeriesLine, { tileHref } from "./SeriesLine";

/**
 * BookTile is one book in the reader's library: its cover, then its title.
 *
 * A grid, not a list. A library is browsed by looking — you recognise a book
 * before you read its name — and a row of text is the shape of a file manager,
 * not of a shelf. The studio keeps its list, because the studio is a to-do
 * list and what matters there is the line saying what still needs doing.
 *
 * Every tile has a picture. A book with no cover gets a generated one rather
 * than an empty frame: a grid with holes in it reads as a loading failure, and
 * the placeholder is stable per book (cover.ts), so the shelf looks the same
 * every time you come back to it.
 *
 * `aspect-[3/4]` on the frame, always, whether the picture has arrived or not.
 * Without it the grid reflows as each image loads, which on a phone moves the
 * tile out from under a thumb already on its way down.
 *
 * `savable` puts the shelf toggle on the cover's corner. It is off by default,
 * and off on the shelf itself: a row of checks saying "yes, still
 * saved" is noise, and the one place a reader wants to unsave from is the book,
 * not a grid they are scanning.
 *
 * `progress` draws how far the reader has got, under the cover (ADR-0061). Off
 * by default, and on only on their own shelf: the catalogue is a list of books
 * to start, and that deferral is the ADR's. The bar is drawn only when there is
 * progress to draw — a grid of empty bars reads as a loading failure, the same
 * reason every tile gets a picture.
 *
 * Every tile badges what reading needs from the server's `access` (ADR-0197):
 * Plus, or Sign in. `face` is a visitor's shelf (the landing page): it also
 * badges the books that open now and lists what each holds — narration,
 * faces, pictures — so a stranger can see what is behind each cover.
 */
export default function BookTile({
  book,
  savable = false,
  progress = false,
  seriesCount = false,
  face = false,
}: {
  book: Book;
  /** A visitor's shelf: the Read now badge too, and the book's counts. */
  face?: boolean;
  savable?: boolean;
  progress?: boolean;
  /** Where a series shows once (Browse): say how many volumes, and open the series (tileHref). */
  seriesCount?: boolean;
}) {
  const t = useT();
  const [from, to] = rampFor(book.id);
  const bar = progress ? barFor(book) : null;
  return (
    <li data-testid="book-tile" className="group relative">
      {savable && <SaveButton book={book} />}
      <Link
        to={tileHref(book, seriesCount)}
        className="block rounded-card focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage"
      >
        <div className="relative aspect-[3/4] overflow-hidden rounded-card bg-sunk shadow-[0_12px_28px_-18px_rgb(0_0_0/0.55)] ring-1 ring-ink/5 transition-shadow group-hover:shadow-[0_18px_36px_-18px_rgb(0_0_0/0.55)]">
          {book.cover_url ? (
            <img
              src={book.cover_url}
              alt=""
              width={book.cover_width}
              height={book.cover_height}
              loading="lazy"
              data-testid="tile-cover"
              className="size-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
            />
          ) : (
            <div
              data-testid="tile-placeholder"
              className="grid size-full place-items-center"
              style={{ background: `linear-gradient(150deg, ${from}, ${to})` }}
            >
              {/* One big letter, not the title: a title set small over a
                  gradient is unreadable at tile size and is about to be
                  printed underneath in full anyway. */}
              <span aria-hidden="true" className="text-5xl font-semibold text-white/85 select-none">
                {initialOf(book.title)}
              </span>
            </div>
          )}
        </div>
        {bar}
        {/* dir="auto" on each authored string and never on a block holding
            two: the title and the author are separate strings and may be in
            different scripts, and an element with dir="auto" skips any
            descendant carrying its own direction — so one on the wrapper would
            leave these two unable to have one (CLAUDE.md). */}
        <div className="mt-2">
          {/* Under the cover, not on it: covers carry their title (owner,
              10-11), and a badge in the corner sat on it. */}
          <AccessBadge book={book} open={face} className="mb-1" />
          <h2
            dir="auto"
            className="line-clamp-2 text-[0.95rem] leading-snug font-semibold group-hover:text-sage-deep"
          >
            {book.title}
          </h2>
          {book.author && (
            <p dir="auto" className="mt-0.5 line-clamp-1 text-xs text-muted">
              {book.author}
            </p>
          )}
          {book.series && (
            <SeriesLine
              series={book.series}
              count={seriesCount}
              testID="tile-series"
              className="mt-0.5 line-clamp-1 text-xs font-medium text-sage-deep"
            />
          )}
          {/* A book shared with a group this reader is in (ADR-0153). */}
          {book.shared_with_you && (
            <p
              data-testid="tile-shared-with-you"
              className="mt-0.5 text-xs font-medium text-sage-deep"
            >
              {t.library.sharedWithYou}
            </p>
          )}
          {face && <FaceFacts book={book} className="mt-1.5" />}
        </div>
      </Link>
    </li>
  );
}

/**
 * barFor is a tile's progress bar, or nothing.
 *
 * Nothing when the viewer is signed out (`chapters_finished` absent), when the
 * book has no chapters a reader is served, and when they have finished none of
 * them: a book they have opened and not finished a chapter of is a book they
 * have barely started, and a bar at 2% on every tile is noise.
 *
 * A separate function rather than a branch inside the markup so the rule is
 * readable as one thing — it is three conditions and they are all about when
 * *not* to draw.
 */
function barFor(book: Book) {
  if (book.finished_at) return <FinishedSeal at={book.finished_at} />;
  const total = book.chapters_total ?? 0;
  const done = book.chapters_finished ?? 0;
  if (total <= 0 || done <= 0) return null;
  return <TileBar done={done} total={total} />;
}

/**
 * FinishedSeal stands where a finished book's full bar would (owner, 10-22):
 * under the cover, never on it, with the month the last chapter was finished.
 * It carries the SageLeaf mark, as the app's does (10-24: the seal is ours).
 * Never a count of books finished.
 */
export function FinishedSeal({ at, className = "mt-2" }: { at: string; className?: string }) {
  const t = useT();
  const month = new Intl.DateTimeFormat(t.meta.dateLocale, {
    month: "short",
    year: "numeric",
  }).format(new Date(at));
  return (
    <p
      data-testid="finished-seal"
      className={`flex items-center gap-1.5 text-xs font-semibold text-sage-deep ${className}`}
    >
      <Logo size={16} className="shrink-0" decorative />
      <span>{t.library.finishedSeal(month)}</span>
    </p>
  );
}

function TileBar({ done, total }: { done: number; total: number }) {
  const t = useT();
  return (
    <ProgressBar
      className="mt-2"
      percent={Math.round((Math.min(done, total) / total) * 100)}
      label={t.library.chaptersFinished(done, total)}
    />
  );
}

/**
 * BookGrid lays the tiles out: two across on a phone, more as there is room.
 *
 * Two, not three. A cover has to be big enough to recognise, and at 360px
 * three columns leave each one about a hundred pixels across — which is a
 * contact sheet, not a shelf.
 */
export function BookGrid({ children, testID }: { children: React.ReactNode; testID?: string }) {
  return (
    <ul
      data-testid={testID}
      className="grid grid-cols-2 gap-x-4 gap-y-6 sm:grid-cols-3 lg:grid-cols-4"
    >
      {children}
    </ul>
  );
}

/**
 * AccessBadge says what would open a book (ADR-0197), from the server's
 * `access` and nothing else: Plus, or Sign in, and — only where `open` asks,
 * on a visitor's shelf — Read now. A tile from before `access` falls back to
 * `locked`, which only ever meant Plus.
 */
export function AccessBadge({
  book,
  open = false,
  className = "",
}: {
  book: Book;
  open?: boolean;
  className?: string;
}) {
  const t = useT();
  const access = book.access ?? (book.locked ? "plus" : undefined);
  if (access === "plus") return <PlusBadge className={className} />;
  if (access === "sign_in")
    return (
      <span data-testid="sign-in-badge" className={`${badge} bg-ink/80 text-ground ${className}`}>
        {t.plus.signInBadge}
      </span>
    );
  if (access === "open" && open)
    return (
      <span data-testid="open-badge" className={`${badge} bg-sage text-on-sage ${className}`}>
        {t.plus.openBadge}
      </span>
    );
  return null;
}

const badge =
  "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[0.7rem] font-semibold backdrop-blur";

/** What a book's face says it holds (ADR-0197): narration, faces, pictures. */
export function FaceFacts({ book, className = "" }: { book: Book; className?: string }) {
  const t = useT();
  const facts = [
    (book.listen_ms ?? 0) > 0
      ? t.plus.narratedHours(Math.max(1, Math.round(book.listen_ms! / 3_600_000)))
      : "",
    (book.portraits_total ?? 0) > 1 ? t.plus.faces(book.portraits_total!) : "",
    (book.pictures_total ?? 0) > 0 ? t.plus.pictures(book.pictures_total!) : "",
  ].filter(Boolean);
  if (facts.length === 0) return null;
  return (
    <ul data-testid="face-facts" className={`flex flex-wrap gap-1 ${className}`}>
      {facts.map((fact) => (
        <li
          key={fact}
          className="rounded-full border border-line px-2 py-px text-[0.7rem] text-muted"
        >
          {fact}
        </li>
      ))}
    </ul>
  );
}

/** PlusBadge marks a tile the reader sees only as its face (ADR-0193). */
export function PlusBadge({ className = "" }: { className?: string }) {
  const t = useT();
  return (
    <span
      data-testid="plus-badge"
      className={`inline-flex items-center gap-1 rounded-full bg-ink/80 px-2 py-0.5 text-[0.7rem] font-semibold text-ground ${className}`}
    >
      <LockIcon className="size-3" />
      {t.plus.badge}
    </span>
  );
}
